/**
 * MemberProfileCard — the member identity block: avatar over a tightly grouped
 * name + member number + membership line (proto Profile.tsx:243-260).
 *
 * Renders:
 * - Avatar circle with initials from displayName
 * - Full display name
 * - Member number + copy button (conditionally rendered if Clipboard API available)
 * - Membership summary line (MembershipSection) as the third line of the group
 * - Icon swap: clipboard → checkmark for ~2s on success
 * - ARIA role="status" live region announcing "Jäsennumero kopioitu" on success
 *
 * No raw colour literals (FR-013) — token classes only.
 * Mobile-first, 375 px safe (FR-012).
 *
 * Owned by feature 018-omat-tiedot.
 * Task: T013
 */

import { type JSX, useEffect, useRef, useState } from 'react';
import { useTranslate } from '../../i18n/useTranslate.js';
import type { MemberProfile } from '../../member-profile/MemberProfilePort.js';
import { deriveAvatarInitials } from '../../shell/avatarInitials.js';
import { CheckIcon, CopyIcon } from '../../shell/icons.js';
import { MembershipSection } from './MembershipSection.js';

export interface MemberProfileCardProps {
  readonly displayName: string;
  readonly profile: MemberProfile;
}

export function MemberProfileCard({ displayName, profile }: MemberProfileCardProps): JSX.Element {
  const t = useTranslate();
  const [isClipboardAvailable, setIsClipboardAvailable] = useState(false);
  const [showCheckmark, setShowCheckmark] = useState(false);

  // Check Clipboard API availability on mount
  useEffect(() => {
    if (typeof navigator !== 'undefined' && navigator.clipboard !== undefined) {
      setIsClipboardAvailable(true);
    }
  }, []);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  const handleCopyMemberNumber = async (): Promise<void> => {
    if (!isClipboardAvailable) return;

    try {
      await navigator.clipboard.writeText(profile.contact.memberNumber);
      setShowCheckmark(true);
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
      }
      timerRef.current = setTimeout(() => {
        setShowCheckmark(false);
        timerRef.current = null;
      }, 2000);
    } catch {
      // Silent failure per RD-006 — button remains visible
      // User just doesn't get the checkmark feedback
    }
  };

  const avatarInitials = deriveAvatarInitials(displayName);

  return (
    <div className="flex flex-col items-center gap-3 text-center">
      {/* Avatar — the prominent identity glyph (proto Profile.tsx:244). The
          negative top margin lifts it so it overlaps the hub hero's top edge
          (the hero supplies the matching top gap); h-20/text-2xl give the proto
          scale. The solid-primary fill is the avatar's identifying token. */}
      <div className="-mt-10 flex h-20 w-20 items-center justify-center rounded-full bg-primary text-2xl font-semibold text-primary-foreground shadow-lg">
        {avatarInitials || <span className="text-primary-foreground">?</span>}
      </div>

      {/* Identity text group — name, member number, and membership line grouped
          TIGHTLY (gap-1) as one block, a compact gap-3 below the avatar (proto
          Profile.tsx:243-260 groups the three lines in a single gap-1 flex; we
          do the same so the header reads as one unit. The avatar sits gap-3
          rather than the proto's gap-4 — owner-directed, the proto's 16px reads
          too airy at our scale once the text block is tight). */}
      <div className="flex flex-col items-center gap-1">
        <div className="text-2xl font-semibold text-foreground">{displayName}</div>

        {/* Member number — a subdued muted line (proto Profile.tsx:248-260): no
            pill/box; the number sits inline next to a quiet copy affordance. */}
        <div className="flex items-center justify-center gap-2">
          <span className="text-sm text-muted-foreground">
            {t('profile.memberNumber.label')}: {profile.contact.memberNumber}
          </span>
          {/* Quiet copy affordance (D-152): a small p-1 icon button, ~32×32px.
              This is a DELIBERATE deviation from the app's 44×44 tap-target
              convention (FR-036 / B-INV-025) — enlarging it would break the
              tight identity grouping above. It still clears the WCAG 2.2 AA
              minimum (24×24). Owner-confirmed 2026-07-01. */}
          {isClipboardAvailable && (
            <button
              type="button"
              onClick={() => {
                void handleCopyMemberNumber();
              }}
              className="rounded-md p-1 transition-colors hover:bg-muted"
              aria-label={t('profile.memberNumber.copyButton')}
              title={t('profile.memberNumber.copyButton')}
            >
              {showCheckmark ? (
                <CheckIcon className="h-5 w-5 text-green-600" aria-hidden="true" />
              ) : (
                <CopyIcon className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
              )}
            </button>
          )}
        </div>

        {/* Membership summary — the third line of the tight identity group.
            IS 010: the live IS profile read now carries the tier + start date;
            MembershipSection renders "{tier} {date} alkaen", or an honest
            "not available" line when the IS omits/can't resolve them. */}
        <MembershipSection tier={profile.membershipTier} startDate={profile.membershipStartDate} />
      </div>

      {/* ARIA live region for copy success announcement. sr-only is
          position:absolute, so it takes no slot in the flex gap above. */}
      <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
        {showCheckmark && t('profile.memberNumber.copied')}
      </div>
    </div>
  );
}
